import React, {useMemo, useRef, useState} from 'react';
import {Template} from "../../../components/template/template.jsx";
import {Card} from "../../../components/card/card.jsx";
import Button from "../../../components/button/button.jsx";
import {FaDownload, FaFileExcel, FaPlus, FaUpload} from "react-icons/fa";
import SelectComponent from "../../../components/select/select-component.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../utils/aggrid-utils.js";
import {useQuery} from "@tanstack/react-query";
import ocrCamsIntegrationService from "../../../services/web/ocr-cams-integration-service.js";
import projectUtils from "../../../utils/project-utils.js";
import {FaGear} from "react-icons/fa6";
import OcrCamsIntegrationConfig from "./components/ocr-cams-integration-config.jsx";
import {toast} from "react-toastify";

const API_KEEPER = import.meta.env.VITE_API_KEEPER;

const OcrCamsIntegration = () => {
    const [state, setState] = useState("");
    const [config, setConfig] = useState(false);
    const [target, setTarget] = useState(undefined);

    const options = useMemo(() => [
        {value: "cortex", label: "CÓRTEX"},
        {value: "prf", label: "PRF"},
        {value: "sspsp", label: "MURALHA PAULISTA"}
    ], []);

    const {data, refetch, isLoading} = useQuery({
        queryFn: () => ocrCamsIntegrationService.list(),
        queryKey: ["ocr-cams"]
    });

    const [columnDefs,] = useState([
        { field: "name", headerName: "Nome"},
        {
            field: "",
            headerName: "Configurações",
            cellRenderer: ({data, setConfig, setTarget}) => {
                const onClick = () => {
                    setTarget(data.name)
                    setConfig(true);
                }

                return (
                    <div className={"h-full w-full flex items-center justify-center"} onClick={onClick}>
                        <FaGear />
                    </div>
                )
            },
            cellRendererParams: {setConfig, setTarget}
        },
        {
            field: "",
            headerName: "Download",
            cellRenderer: ({data, options}) => {
                const onClick = () => {
                    const find = options.find(item => item.label === data.name);
                    if (!find) {
                        projectUtils.swalErrorMessage("", "Integração não encontrada para download.", "Ok");
                        return
                    }

                    const url = `${API_KEEPER}/integrations/web/lane/export/${find.value}`;

                    fetch(url)
                        .then(response => response.blob())
                        .then(blob => {
                            const url = URL.createObjectURL(blob);
                            window.open(url)
                            URL.revokeObjectURL(url);
                        })
                        .catch(() => projectUtils.swalErrorMessage("", "Erro ao fazer download do arquivo.", "Ok"));
                }

                return (
                    <div className={"h-full w-full flex items-center justify-center"} onClick={onClick}>
                        <Button>
                            <FaDownload />
                        </Button>
                    </div>
                )
            },
            cellRendererParams: {options}
        },
        {
            field: "",
            headerName: "Upload",
            cellRenderer: ({data, options}) => {
                const inputFile = useRef(undefined);

                const handleFile = ({target: {files}}) => {
                    const file = files[0];
                    if (!file) return;

                    if (file && (!file.name.endsWith(".xlsx") || file.type !== "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")) {
                        projectUtils.swalErrorMessage("", "Tipo de arquivo  inválido");
                        return;
                    }

                    const find = options.find(item => item.label === data.name);
                    if (!find) {
                        projectUtils.swalErrorMessage("", "Integração não encontrada para upload.", "Ok");
                        return
                    }

                    const formData = new FormData();
                    formData.append("file", file);
                    ocrCamsIntegrationService.import(find.value, file)
                        .then(() => {
                            toast.success("Arquivo importado com sucesso")
                        })
                        .catch(error => {
                            console.error(error.response)
                            projectUtils.swalErrorMessage('', 'Não foi possível importar a integração.', 'Ok');
                        })
                        .finally(() => inputFile.current.value = "")
                }

                return (
                    <div className={"h-full w-full flex items-center justify-center"} onClick={() => inputFile?.current.click()}>
                        <input type="file" hidden accept={".xlsx"} ref={inputFile} onChange={handleFile}/>
                        <Button>
                            <FaUpload />
                        </Button>
                    </div>
                )
            },
            cellRendererParams: {options}
        }
    ]);

    const gridRef = useRef(undefined);

    const addIntegration = () => {
        if (!state) {
            projectUtils.swalErrorMessage("", "O campo \"Nome da integração\" é obrigatório.", "Ok");
            return;
        }

        if (data.some(item => item.name === state)) {
            projectUtils.swalErrorMessage("", "Integração já está adicionada.", "Ok");
            return;
        }

        ocrCamsIntegrationService.save(state, {lanePrefix: "", properties: {}})
            .then(() => {
                refetch();
            })
            .catch(error => {
                let message = 'Não foi possível inserir a integração.';
                if (error.response && error.response.status !== 500) {
                    message = error.response.data.message;
                }
                projectUtils.swalErrorMessage('', message, 'Ok');
            })
    }

    const exportLane = () => {
        const url = `${API_KEEPER}/integrations/web/general/lane/export`;

        fetch(url)
            .then(response => response.blob())
            .then(blob => {
                const url = URL.createObjectURL(blob);
                window.open(url)
                URL.revokeObjectURL(url);
            })
            .catch(() => projectUtils.swalErrorMessage("", "Erro ao exportar.", "Ok"));
    }

    return (
        <>
            <Template isLoading={isLoading}>
                <Template.Header>
                    <Template.Title>Câmeras OCR e Integrações</Template.Title>

                    <Button target={"success"} onClick={exportLane}>
                        <FaFileExcel /> Exportar lista câmeras OCR
                    </Button>
                </Template.Header>

                <Template.Body className={"space-y-2"}>
                    <Card>
                        <Card.Body>
                            <div className="flex justify-between gap-4 w-1/4">
                                <SelectComponent
                                    classNameWrapper={"w-full"}
                                    value={state}
                                    options={options}
                                    label={"Nome da integração"}
                                    onChange={(value) => setState(value?.value)}
                                />

                                <Button onClick={addIntegration} title={"Adicionar"}>
                                    <FaPlus />
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>

                    <Card>
                        <Card.Body>
                            <div className={"aggrid-row-pointer"} style={{height: "calc(100vh - 153px)"}}>
                                <AgGridReact
                                    ref={gridRef}
                                    rowData={data ?? []}
                                    columnDefs={columnDefs}
                                    animateRows={false}
                                    suppressCellFocus={true}
                                    defaultColDef={{flex: 1}}
                                    gridOptions={{localeText: aggridUtils.localization()}}
                                    theme={aggridUtils.generalTheme({rowHeight: 35})}
                                />
                            </div>
                        </Card.Body>
                    </Card>
                </Template.Body>
            </Template>

            <OcrCamsIntegrationConfig
                data={data}
                target={target}
                open={config}
                setOpen={setConfig}
                options={options}
                refetch={refetch}
            />
        </>
    );
};

export default OcrCamsIntegration;